import {
  useDebounce,
  useOnChange,
  useStableCallback,
} from "@prismicio/editor-support/React"
import {
  Box,
  FileUploadButton,
  FormSearchInput,
  Portal,
  Tooltip,
} from "@prismicio/editor-ui"
import { type ReactNode, useState } from "react"

import { textConstants } from "../constants/text"
import { useMediaLibrary } from "./MediaLibraryContext"

export function MediaLibraryActions() {
  const { actionContainer, isUploading, onFilesUpload, assetType } =
    useMediaLibrary()

  return (
    <MediaLibraryActionsContainer actionContainer={actionContainer}>
      <Box width="100%" gap={16}>
        <SearchBar />
        <Tooltip
          title={textConstants.warning.uploadWhileUploading.title}
          content={textConstants.warning.uploadWhileUploading.description}
          visible={isUploading}
          stableMount
        >
          <FileUploadButton
            disabled={isUploading}
            onFilesSelected={onFilesUpload}
            startIcon="cloudUpload"
            color={actionContainer ? "purple" : "grey"}
            size="large"
            assetType={assetType}
          >
            Upload media
          </FileUploadButton>
        </Tooltip>
      </Box>
    </MediaLibraryActionsContainer>
  )
}

function SearchBar() {
  const { keyword, onFilterChange, isFetchingMedia } = useMediaLibrary()

  const [inputValue, setInputValue] = useState(keyword)

  // Respect a reset request
  useOnChange(keyword, () => {
    if (keyword === "") setInputValue("")
  })

  useDebounce(
    inputValue,
    300,
    useStableCallback((inputValue) => onFilterChange({ keyword: inputValue })),
  )

  return (
    <Box width="100%" flexDirection="column">
      <FormSearchInput
        value={inputValue}
        placeholder="Search"
        onValueChange={setInputValue}
        maxLength={200}
        isLoading={isFetchingMedia && keyword !== inputValue}
      />
    </Box>
  )
}

interface MediaLibraryActionsContainerProps {
  children: ReactNode
  actionContainer?: Element | null
}

export function MediaLibraryActionsContainer(
  props: MediaLibraryActionsContainerProps,
) {
  const { children, actionContainer } = props

  if (actionContainer) {
    return (
      <Portal container={actionContainer} asChild>
        {children}
      </Portal>
    )
  }

  return <>{children}</>
}
